import type { SquaresFixtureScoreSnapshot, SquaresMyPick } from '../api/getMyGames';

/**
 * Scores for one Squares card on My Games — row/col order matches the team names the card
 * already shows (`row_team_display_name` / `col_team_display_name`).
 *
 * Mirrored from the Squares app's My Picks mapping so Live and Recent read the same numbers as
 * the Squares host app for the same contest.
 */
export type SquaresCardScores = {
  rowScore: number;
  colScore: number;
};

export function squaresContestScoreId(pick: SquaresMyPick): string | null {
  const id = (pick.question_id as string | undefined) ?? pick.squares_contest_id;
  return id ? String(id) : null;
}

/**
 * Map a fixture snapshot onto the card's row/col teams.
 *
 * Row/col are NOT always home/away — the contest stores which team is on each axis — so match
 * by team id when both sides are known, and fall back to home→row / away→col when they are not.
 */
export function mapSquaresFixtureScores(
  pick: SquaresMyPick,
  fixture: SquaresFixtureScoreSnapshot | null | undefined
): SquaresCardScores | null {
  if (!fixture) return null;

  const homeScore = fixture.homeScore ?? 0;
  const awayScore = fixture.awayScore ?? 0;
  const rowTeamId = pick.row_team_id as string | undefined;
  const colTeamId = pick.col_team_id as string | undefined;
  const homeId = fixture.homeTeam?.id;
  const awayId = fixture.awayTeam?.id;

  const rowScore =
    rowTeamId && homeId && rowTeamId === homeId
      ? homeScore
      : rowTeamId && awayId && rowTeamId === awayId
        ? awayScore
        : homeScore;

  const colScore =
    colTeamId && awayId && colTeamId === awayId
      ? awayScore
      : colTeamId && homeId && colTeamId === homeId
        ? homeScore
        : awayScore;

  return { rowScore, colScore };
}
